<template>
  <v-card :color="appColor_GenieExtraLight">
    
    <!-- Main Toolbar -->
    <v-toolbar dense :color="toolbarColor" dark flat>
      
      <v-toolbar-title :class="titleClass"><b>{{ title }}</b></v-toolbar-title>

      <v-spacer />

      <v-toolbar-items>
        <v-btn
          @click="done"
          large
          :color="toolbarColorDark"
          class="elevation-0"
          >
        <span class="subheading font-weight-medium">{{ getExpression('Button', 'Close') }}</span></v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <!-- Azure Message_Id Panel -->
    <v-card v-if="isAdmin" :class="messageIdPanelClass">
      <v-card-title :class="panelTitleClass">Message Id</v-card-title>
      <v-card-text>
        {{ kioskEmail.messageId }}
        <v-btn
          icon
          v-clipboard:copy="kioskEmail.messageId"
          v-clipboard:success="onCopy"
          class="ml-2"
        ><v-icon>content_copy</v-icon></v-btn>
      </v-card-text>
    </v-card>

    <!-- Sender Panel -->
    <v-card :class="senderPanelClass">
      <v-card-title :class="panelTitleClass">{{ getExpression('Label', 'Sender') }}</v-card-title>
      <v-card-text>
        {{ getSenderName(kioskEmail.sender) }}
      </v-card-text>
    </v-card>

    <!-- Recipients Panel -->
    <v-card :class="panelClass">
      <v-card-title :class="panelTitleClass">{{ recipientExpression }}</v-card-title>
      <v-card-text>
        {{ recipientList }}
      </v-card-text>
    </v-card>

    <!-- Subject Panel -->
    <v-card :class="panelClass">
      <v-card-title :class="panelTitleClass">{{ getExpression('Label', 'Subject') }}</v-card-title>
      <v-card-text>
        {{ kioskEmail.subject }}
      </v-card-text>
    </v-card>

    <!-- Message Panel -->
    <v-card :class="panelClass">
      <v-card-title :class="panelTitleClass">{{ getExpression('Label', 'Message') }}</v-card-title>
      <v-card-text v-html="emailMessage">
      </v-card-text>
    </v-card>

    <!-- Attachments Panel -->
    <v-card v-if="blobs.length > 0" class="mx-2 pb-2">
      <v-card-title :class="panelTitleClass">{{ getExpression('Label', 'Attachments') }}</v-card-title>
    
      <!-- Data Table -->
      <v-card class="mx-2">
        <v-card-text class="pt-0 pb-1">
          <v-data-table
            :headers="headers"
            :items="blobs"
            :loading="isLoading"
            :search="search"
            :no-data-text="noDataText"
            :hide-default-footer="true"
            class="pt-0"
            >

            <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

            <template v-slot:item.thumbnail="{ item }">
              <router-link :to="currentRoute" style="text-decoration: none">
                <span @click="preview(item)">
                  <v-img v-if="getMediaType(item.contentType) !== 'audio'" width="50" height="50" :src="getThumbnailUrl(item)" alt=""></v-img>
                  <v-icon v-else large color="blue darken-2">volume_up</v-icon>
                </span>
              </router-link>
            </template>

            <template v-slot:item.contentType="{ item }">
              <app-content-type v-bind:contentType="item.contentType" />
            </template>

            <template v-slot:item.createDate="{ item }">
              <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
            </template>

          </v-data-table>
        </v-card-text>
      </v-card>
    </v-card>

    <!-- Media Previewer -->
    <v-dialog
      v-model="previewDialog"
      max-width="800px"
      persistent
      >
      <media-previewer
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-bind:title="previewTitle"
        v-on:close="closePreview"
      />
    </v-dialog>
  
    <!-- Clipboard Copy Snackbar-->
    <v-snackbar v-model="copySnackbar" :timeout="2000">
      Copied!
      <v-btn dark text @click="copySnackbar = false" :color="appColor_Medium">
        Close
      </v-btn>
    </v-snackbar>

  </v-card>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import MediaPreviewer from '@/components/MediaPreviewer'
import { AppColor, Base64Image, ThumbnailUrl } from '@/config.js'
import AppContentType from '@/components/App/AppContentType'
import AppDate from '@/components/App/AppDate'
  
export default {
  components: {
    MediaPreviewer,
    AppContentType,
    AppDate
  },
  name: 'PlaylistDetail',
  props: {
    id: {
      type: Number,
      default: -1
    },
    controlId: {
      type: String,
      default: null
    },
    kioskEmail: {
      type: Object,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_GenieExtraLight: AppColor.KioskTypeGenieExtraLight,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

            // uri
      thumbnailUrl_Genie: ThumbnailUrl.Genie,

      isLoading: false,
      isSaving: false,
      copySnackbar: false,
      previewDialog: false,
      previewType: null,
      previewTitle: null,
      previewUrl: null,

      mediaType: 'all',
      search: ''
    }
  },
  computed: {
    ...mapGetters(['currentRoute', 'isAdmin', 'profile']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      return this.blobs.length === 0
        ? this.getExpression('Alert', 'NoMedia')
        : ''
    },
    title () {
      return `${this.getExpression('Heading', 'EmailDetail')} - ${this.formattedDate}`
    },
    formattedDate () {
      const date = this.kioskEmail.createDate
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('ddd, MMM DD, YYYY')
        }
      }
      return null
    },
    emailMessage () {
      if (this.kioskEmail?.message === null) return ''
      return this.kioskEmail.message.replace('\n', '<br/>')
    },
    recipientList () {
      if (this.users.length === 0) return null

      const users = this.users
      const first = users[0]
      const firstText = `${first.nickname} (${first.email})`
      if (users.length === 1) return firstText

      var list = firstText
      users.filter(u => u.email !== first.email).forEach(u => {
        list = `${list}, ${u.nickname} (${u.email})`
      })
      return list
    },
    recipientExpression () {
      return this.kioskEmail?.users.length === 1
        ? this.getExpression('Label', 'Recipient')
        : this.getExpression('Label', 'Recipients')
    },
    blobs () {
      return this.kioskEmail?.blobs ?? []
    },
    users () {
      return this.kioskEmail?.users ?? []
    },
    headers () {
      var headers = []
      headers.push({
        value: 'thumbnail',
        align: 'left',
        width: '3%',
        sortable: false,
        class: "pr-0"
      })

      headers.push({
        value: 'contentType',
        align: 'left',
        width: '2%',
        class: 'pr-0'
      })

      headers.push({
        text: this.getExpression('Label', 'Title'),
        value: 'title',
        align: 'left',
        width: '45%',
        class: 'pr-0'
      })

      headers.push({
        text: this.getExpression('Label', 'Description'),
        value: 'description',
        align: 'left',
        width: '50%',
        class: 'pr-0'
      })
    
      return headers
    },
    titleClass () {
      return 'title'
    },
    panelClass () {
      return 'mb-2 mx-2'
    },
    messageIdPanelClass () {
      return 'ma-2'
    },
    senderPanelClass () {
      return 'ma-2'
    },
    panelTitleClass () {
      return 'subtitle-1 pb-0'
    }
  },
  methods: {
    done () {
      this.$emit('done')
    },

    preview (item) {
      const self = this
      appService.getBlobUrl(item.containerName, item.blobName).then(data => {
        const mediaType = this.getMediaType(item.contentType)
        self.previewUrl = data
        self.previewType = mediaType
        self.previewTitle = item.title
        self.previewDialog = true
      }).catch(e => { self.handleCriticalError(e) })
    },

    closePreview () {
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },

    getThumbnailUrl (item) {
      const mediaType = this.getMediaType(item.contentType)
      if (mediaType !== 'audio') {
        const blobName = item.blobName
        const ext = blobName.split('.').pop()
        const thumnailName = blobName.replace(ext, 'jpg')

        const url = `${this.thumbnailUrl_Genie}/${item.containerName}/${thumnailName}`.replace('#', '%23')
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url

        return url
      }
    },

    getSenderName (sender) {
      if (this.isNullOrUndefined(sender)) return ''
      return sender.id === this.profile.id
        ? `${sender.nickname} (You)`
        : sender.nickname
    },

    onCopy () {
      this.copySnackbar = true
    }
  },
  watch: {
    id (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.mediaType = 'all'
      }
    }
  }
}
</script>
  